import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ContextActionType, useDumbActionsContext } from '@europrocurement/flexy-components';
import {
    ColumnDatatable,
    FilterDatatable,
    FilterOptionValue,
    FiltersDatatableList,
} from '@europrocurement/flexy-datatable';

import { PackageJsonldPackageRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { addItem, FaOptionIcon } from '@europrocurement/l2d-icons';
import { useOffreService } from '@europrocurement/l2d-domain';
import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import RoutedListing from '../components/RoutedListing';
import { displayCustomValue, getStringColumn } from '../listings';
import { PossibleTypes } from '../../Societes';
import useFilterOptions from '../Catalog/useFilterOptions';
import { packageCreatePath, packageFichePath } from './routes';
import EllipsisCell from '../components/EllipsisCell';
import LinkToColumn from '../Catalog/Component/LinkToColumn';

const Panel: React.FunctionComponent = function () {
    const navigate = useNavigate();
    const { offerPackageModel: model } = useOffreService();
    const filterOptions = useFilterOptions();

    const goToCreate = React.useCallback(() => navigate(packageCreatePath), [navigate]);
    const goToFiche = React.useCallback(
        (item: { id: string | number }) => navigate(packageFichePath(item.id)),
        [navigate],
    );

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Créer un nouveau package',
                icon: <FaOptionIcon icon={['fas', 'plus-circle']} />,
                action: goToCreate,
            },
        ],
        [goToCreate],
    );

    useDumbActionsContext(contextActions);

    const columns: ColumnDatatable<PackageJsonldPackageRead>[] = [
        {
            ...getStringColumn('libelle', 'Libellé'),
            sortable: true,
        },
        {
            ...getStringColumn('nomCommercial', 'Nom commercial'),
            sortable: true,
        },
        {
            ...displayCustomValue(
                '' as keyof PackageJsonldPackageRead['formalite'],
                (formality: PackageJsonldPackageRead['formalite']) => formality?.libelle ?? '---',
                '',
            ),

            label: 'Type de formalité',
            displayLabel: true,
            render: (item) => {
                const cataloguePackageFormality = item?.formalite as
                    | { libelle?: string; id?: number }
                    | undefined;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '0.2em' }}>
                        <EllipsisCell
                            text={cataloguePackageFormality?.libelle ?? '(aucun package)'}
                        />
                        {item.formalite?.id ? (
                            <LinkToColumn
                                link={`/formalites/liste/${item.formalite.id}`}
                                tooltip="Ouvrir la formalité"
                            />
                        ) : null}
                    </Box>
                );
            },
            isDisplayed: true,
            sortable: false,
            field: 'formalite',
        },
        {
            label: 'Domaine',
            displayLabel: true,
            render: (item) => {
                const domain = item.domaine as { libelle?: string };
                return (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {domain?.libelle ?? '(aucun domaine)'}
                    </Typography>
                );
            },
            isDisplayed: true,
            sortable: false,
            field: 'domaine',
        },
    ];

    /** Generic function : Generate filter object */
    const getFilter = (
        label: string,
        field: string,
        options: FilterOptionValue[],
    ): FilterDatatable => ({
        label,
        field,
        type: 'staticSelect' as const,
        options,
        renderFilterList: (value: string) => {
            const selectedOption = options.find((option) => option.value === value);
            return selectedOption ? selectedOption.label : 'Aucun';
        },
    });

    const filters: FiltersDatatableList = [
        getFilter('Domaine', 'domaineId', filterOptions.domains),
        {
            field: 'nomCommercial',
            label: 'Nom commercial',
            type: 'text',
        },
        {
            field: 'libelle',
            label: 'Libellé',
            type: 'text',
        },
    ];

    return (
        <RoutedListing
            listingTitle="Liste des packages"
            createButton={{
                label: 'Créer un nouveau package',
                icon: addItem,
                onClick: goToCreate,
            }}
            listingColumns={columns as ColumnDatatable<unknown>[]}
            model={model}
            filters={filters}
            filtersControl
            searchBarType={PossibleTypes.Package}
            onRowClick={goToFiche}
        />
    );
};

export default Panel;
